import {
  Component,
  Inject,
  OnInit,
  ChangeDetectionStrategy,
} from '@angular/core'
import {
  FormBuilder,
  FormControl,
  ReactiveFormsModule,
  Validators,
} from '@angular/forms'
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog'
import { MatIcon } from '@angular/material/icon'
import { MatSnackBar } from '@angular/material/snack-bar'
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy'
import { catchError, finalize, forkJoin, of, tap } from 'rxjs'

import { ButtonComponent } from '../../../../components/button/button.component'
import { LoadingComponent } from '../../../../components/loading/loading.component'
import { AssessmentService } from '../../../../services/assessment/assesssment.service'
import {
  AssessmentTagMaster,
  SkillMap,
} from '../../../../services/assessment/assessment.mapping'
import {
  BlueBeanIntegrationSettingView,
  ExternalIntegrationService,
} from '../../../../services/external-integration/external-integration.service'
import { captureException } from '../../../../util/captureException'
import {
  CustomError,
  CustomErrorCode,
} from '../../../../util/throwIfCustomError'

export type BlueBeanIntegrationData = {
  setting: BlueBeanIntegrationSettingView | null
}

export type BlueBeanIntegrationResult = BlueBeanIntegrationSettingView

const BLUEBEAN_CREDENTIAL_PARTIAL_ERROR_MESSAGE =
  'ドメイン・ユーザー名・パスワードは 3 つ全て入力するか全て空欄にしてください'

@UntilDestroy()
@Component({
  selector: 'app-bluebean-integration',
  standalone: true,
  imports: [ReactiveFormsModule, MatIcon, ButtonComponent, LoadingComponent],
  templateUrl: './bluebean-integration.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrl: './bluebean-integration.component.scss',
})
export class BlueBeanIntegrationComponent implements OnInit {
  skillMaps: SkillMap[] = []
  assessmentTagMasters: AssessmentTagMaster[] = []
  isLoading = false
  isPasswordVisible = false
  isEditMode = false
  skillMapLoadError: string | null = null
  tagMasterLoadError: string | null = null
  isCurrentTagDisabled = false

  domain = new FormControl<string>('', {
    nonNullable: true,
    validators: [Validators.required],
  })
  username = new FormControl<string>('', {
    nonNullable: true,
    validators: [Validators.required],
  })
  password = new FormControl<string>('', {
    nonNullable: true,
    validators: [Validators.required],
  })
  campaignIdsInput = new FormControl<string>('', {
    nonNullable: true,
    validators: [Validators.required],
  })
  skillMapId = new FormControl<string>('', {
    nonNullable: true,
    validators: [Validators.required],
  })
  defaultTagMasterId = new FormControl<string>('', { nonNullable: true })

  formGroup = this.formBuilder.group({
    domain: this.domain,
    username: this.username,
    password: this.password,
    campaignIdsInput: this.campaignIdsInput,
    skillMapId: this.skillMapId,
    defaultTagMasterId: this.defaultTagMasterId,
  })

  constructor(
    @Inject(MAT_DIALOG_DATA) public data: BlueBeanIntegrationData,
    private formBuilder: FormBuilder,
    private dialogRef: MatDialogRef<BlueBeanIntegrationComponent>,
    private snackBar: MatSnackBar,
    private externalIntegrationService: ExternalIntegrationService,
    private assessmentService: AssessmentService,
  ) {}

  ngOnInit(): void {
    const setting = this.data.setting
    this.isEditMode = setting?.active === true
    this.configureCredentialValidators()
    if (setting) {
      this.campaignIdsInput.setValue(setting.campaignIds.join(', '))
      this.skillMapId.setValue(setting.skillMapId ?? '')
    }

    this.isLoading = true
    forkJoin({
      skillMaps: this.assessmentService.getSkillMaps().pipe(
        catchError((error: unknown) => {
          captureException(error)
          // スキルマップは必須項目のため、取得失敗時はドロップダウンが空のまま
          // 保存できず手詰まりになる。消える snackBar ではなくダイアログ内に
          // 持続表示し、再読込導線を伝える (Kaikura と同型)。
          this.skillMapLoadError =
            'スキルマップの取得に失敗しました。ページを再読込してください。'
          return of([])
        }),
      ),
      tagMasters: this.assessmentService.getAssessmentTagMasters().pipe(
        catchError((error: unknown) => {
          captureException(error)
          this.tagMasterLoadError =
            'タグ一覧の取得に失敗しました。ページを再読込してください。'
          // null で取得失敗を表す (空配列は正常0件と区別する)
          return of(null)
        }),
      ),
    })
      .pipe(
        tap(({ skillMaps, tagMasters }) => {
          this.skillMaps = skillMaps
          if (tagMasters == null) {
            return
          }
          this.assessmentTagMasters = tagMasters
          const defaultTagMasterId = setting?.defaultTagMasterId
          if (!defaultTagMasterId) {
            return
          }
          const isKnownTagMaster = tagMasters.some(
            (tagMaster) => tagMaster.id === defaultTagMasterId,
          )
          if (isKnownTagMaster) {
            this.defaultTagMasterId.setValue(defaultTagMasterId)
          } else {
            this.isCurrentTagDisabled = true
          }
        }),
        finalize(() => {
          this.isLoading = false
        }),
        untilDestroyed(this),
      )
      .subscribe()
  }

  /**
   * カンマ区切り文字列 "1, 2, 3" を number[] に変換する。
   * 空 / 不正値が混入した場合は null を返し呼び出し元でエラー表示させる
   * (silent な数値捨てを防ぐ)。
   */
  parseCampaignIds(input: string): number[] | null {
    const trimmed = input.trim()
    if (trimmed === '') {
      return null
    }
    const tokens = trimmed.split(',').map((token) => token.trim())
    const ids: number[] = []
    for (const token of tokens) {
      if (!/^\d+$/.test(token)) {
        return null
      }
      ids.push(Number(token))
    }
    return ids
  }

  onSubmit(event: Event): void {
    event.preventDefault()
    // タグ一覧取得失敗のまま保存すると、既存タグ設定を意図せず解除してしまう。
    if (this.tagMasterLoadError != null) {
      return
    }
    this.formGroup.markAllAsTouched()

    const credentials = this.getTrimmedCredentials()
    const credentialInputCount = Object.values(credentials).filter(
      (value) => value !== '',
    ).length

    if (
      this.isEditMode &&
      credentialInputCount !== 0 &&
      credentialInputCount !== 3
    ) {
      this.snackBar.open(BLUEBEAN_CREDENTIAL_PARTIAL_ERROR_MESSAGE, '閉じる', {
        duration: 4000,
      })
      return
    }

    if (this.formGroup.invalid) {
      this.snackBar.open('入力内容を確認してください。', '閉じる', {
        duration: 4000,
      })
      return
    }

    const campaignIds = this.parseCampaignIds(this.campaignIdsInput.value)
    if (campaignIds == null) {
      this.snackBar.open(
        '業務ID は数字のカンマ区切りで指定してください。',
        '閉じる',
        { duration: 4000 },
      )
      return
    }

    this.isLoading = true
    this.externalIntegrationService
      .saveBlueBeanIntegration({
        domain: credentials.domain,
        username: credentials.username,
        password: credentials.password,
        campaignIds,
        skillMapId: this.skillMapId.value,
        defaultTagMasterId:
          this.defaultTagMasterId.value === ''
            ? null
            : this.defaultTagMasterId.value,
      })
      .pipe(
        tap((result) => {
          this.snackBar.open('BlueBean 連携を保存しました。', '閉じる', {
            duration: 4000,
          })
          this.dialogRef.close({
            __typename: 'BlueBeanIntegrationSetting',
            id: result.id,
            orgId: result.orgId,
            active: result.active,
            skillMapId: result.skillMapId ?? null,
            campaignIds: result.campaignIds,
            defaultTagMasterId: result.defaultTagMasterId ?? null,
          } satisfies BlueBeanIntegrationResult)
        }),
        catchError((error: unknown) => {
          captureException(error)
          const message =
            error instanceof CustomError &&
            error.code === CustomErrorCode.INVALID_BLUE_BEAN_CREDENTIALS
              ? error.message
              : 'BlueBean 連携の保存に失敗しました。接続情報を確認してください。'
          this.snackBar.open(message, '閉じる', { duration: 6000 })
          return of(null)
        }),
        finalize(() => {
          this.isLoading = false
        }),
        untilDestroyed(this),
      )
      .subscribe()
  }

  close(): void {
    this.dialogRef.close()
  }

  /**
   * 編集モード（既存設定あり）では credential 全空欄での「設定のみ更新」を許容するため
   * required を外す。新規モードでは 3 つ必須を維持する。
   */
  private configureCredentialValidators(): void {
    const validators = this.isEditMode ? [] : [Validators.required]
    this.domain.setValidators(validators)
    this.username.setValidators(validators)
    this.password.setValidators(validators)
    this.domain.updateValueAndValidity()
    this.username.updateValueAndValidity()
    this.password.updateValueAndValidity()
  }

  private getTrimmedCredentials(): {
    domain: string
    username: string
    password: string
  } {
    return {
      domain: this.domain.value.trim(),
      username: this.username.value.trim(),
      password: this.password.value.trim(),
    }
  }
}
